import { useLocation } from "react-router-dom";
import { useStrapiConfig } from "./useStrapiConfig";
import { UseLoginPageResult } from "types";
import { getPlatform, setPlatform, getApplicationId, setApplicationId, getConfigId, setConfigId } from "../storage";
import { defaultConfigId } from "env";

// `platform`/`tenant` are expected alongside config_id on the URL. When
// present they're persisted (URL wins over whatever was previously stored)
// so every API call — including ones triggered from a page reached without
// the param — can still tag itself with them. Runs synchronously during
// render, not in an effect, so they're captured before useStrapiConfig's
// fetch fires.
function resolveStoredParam(
  search: string,
  key: string,
  getStored: () => string | null,
  setStored: (value: string) => void,
): string | null {
  const fromUrl = new URLSearchParams(search).get(key);
  if (fromUrl) {
    setStored(fromUrl);
    return fromUrl;
  }
  return getStored();
}

// Every auth page needs the same things: the tenant's config_id, platform,
// and tenant from the URL, and the raw Strapi config. This was previously
// copy-pasted (with the config_id extraction duplicated again inside
// useStrapiConfig itself) into every page component.
export function useLoginPage(): UseLoginPageResult {
  const location = useLocation();
  const resolvedConfigId = resolveStoredParam(location.search, "config_id", getConfigId, setConfigId);
  const configId = resolvedConfigId ?? defaultConfigId;
  // Persist the env-level fallback too — otherwise anything reading
  // storage directly (e.g. analytics.service.ts's commonParams) sees an
  // empty config_id even though every page is correctly using
  // defaultConfigId in memory.
  if (!resolvedConfigId) setConfigId(defaultConfigId);
  const platform = resolveStoredParam(location.search, "platform", getPlatform, setPlatform);

  // `application_id`/`app_id` are expected as one-or-the-other on the URL —
  // persisted to localStorage (see storage.ts) so every API call still tags
  // itself correctly even on a page reached without the param on the URL.
  const applicationIdParam = new URLSearchParams(location.search).get("application_id") ?? new URLSearchParams(location.search).get("app_id");
  if (applicationIdParam) setApplicationId(applicationIdParam);
  const applicationId = applicationIdParam ?? getApplicationId();
  const { data: loginPageConfig, isLoading } = useStrapiConfig();

  return { configId, platform, applicationId, loginPageConfig, isLoading };
}
